import DataTable from "../components/common/DataTable";
import { useQuery } from "@tanstack/react-query";
import { Paper, Loader, Tabs, Button, Group, Progress, Badge, SimpleGrid, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { Download, FileSpreadsheet, FileText, ArrowUp, ArrowDown } from "lucide-react";
import dayjs from "dayjs";
import React from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { axiosInstance } from "../utils/api";

// Color palette for charts
const COLORS = ["#1890ff", "#52c41a", "#faad14", "#f5222d", "#13c2c2", "#722ed1"];

// Revenue trend data
const generateRevenueData = () => [
  { date: "Jan 1", daily: 15420, monthly: 425620, users: 125420 },
  { date: "Jan 5", daily: 18560, monthly: 450890, users: 132450 },
  { date: "Jan 10", daily: 22340, monthly: 485670, users: 140230 },
  { date: "Jan 15", daily: 19870, monthly: 498340, users: 145670 },
  { date: "Jan 20", daily: 24560, monthly: 520450, users: 152340 },
  { date: "Jan 25", daily: 21450, monthly: 535690, users: 158900 },
  { date: "Jan 30", daily: 25670, monthly: 550120, users: 165420 },
];

// User growth data
const generateUserGrowthData = () => [
  { date: "Week 1", free: 15420, paid: 8450, premium: 3200 },
  { date: "Week 2", free: 18900, paid: 11230, premium: 4120 },
  { date: "Week 3", free: 22340, paid: 14560, premium: 5670 },
  { date: "Week 4", free: 25680, paid: 18340, premium: 7290 },
];

// Device distribution
const generateDeviceDistribution = () => [
  { name: "Mobile", value: 65, users: 81523 },
  { name: "Web", value: 20, users: 25084 },
  { name: "TV App", value: 10, users: 12542 },
  { name: "Tablet", value: 5, users: 6271 },
];

// Content performance
const generateContentPerformanceData = () => [
  { name: "Action", views: 125680, revenue: 15420 },
  { name: "Drama", views: 98450, revenue: 12340 },
  { name: "Comedy", views: 87230, revenue: 10980 },
  { name: "Horror", views: 65430, revenue: 8230 },
  { name: "Romance", views: 54320, revenue: 6850 },
];

// Subscription trend
const generateSubscriptionTrendData = () => [
  { month: "Jan", basic: 28450, premium: 12680, annual: 8920, family: 5450 },
  { month: "Feb", basic: 31230, premium: 14450, annual: 10230, family: 6340 },
  { month: "Mar", basic: 33890, premium: 16780, annual: 12340, family: 7890 },
  { month: "Apr", basic: 36450, premium: 18920, annual: 14560, family: 9230 },
];

// Payment methods
const generatePaymentMethodData = () => [
  { name: "Stripe", value: 35, transactions: 24580 },
  { name: "PayPal", value: 28, transactions: 18920 },
  { name: "M-Pesa", value: 22, transactions: 15680 },
  { name: "Cards", value: 15, transactions: 12450 },
];

// Generate dummy KPIs
const generateDummyKPIs = () => ({
  totalUsers: 125420,
  userChange: 12.5,
  dau: 45320,
  dauChange: 8.3,
  wau: 89560,
  wauChange: 5.2,
  mau: 125420,
  mauChange: 3.1,
  totalSubscribers: 85600,
  subscriberChange: 15.8,
  freeUsers: 39820,
  paidUsers: 45780,
  revenueDaily: 15420.50,
  revenueChange: 22.5,
  revenueMonthly: 425620.00,
  revenueYearly: 5107440.00,
  totalWatchHours: 2456780,
  avgWatchTime: 45.5,
  conversionRate: 12.8,
  conversionChange: 4.2,
});

// Dummy data for various analytics tables
const contentAnalytics = [
  {
    key: "1",
    title: "The Last Kingdom",
    type: "Series",
    views: 425680,
    uniqueViewers: 385200,
    watchTime: 2145680,
    completionRate: "78%",
    avgRating: 4.5,
  },
  {
    key: "2",
    title: "Avatar 2",
    type: "Movie",
    views: 385420,
    uniqueViewers: 345890,
    watchTime: 1925640,
    completionRate: "85%",
    avgRating: 4.7,
  },
  {
    key: "3",
    title: "Wednesday",
    type: "Series",
    views: 325680,
    uniqueViewers: 298450,
    watchTime: 1856450,
    completionRate: "72%",
    avgRating: 4.3,
  },
  {
    key: "4",
    title: "Oppenheimer",
    type: "Movie",
    views: 298560,
    uniqueViewers: 267890,
    watchTime: 1492800,
    completionRate: "88%",
    avgRating: 4.8,
  },
];

const paymentAnalytics = [
  {
    key: "1",
    gateway: "Stripe",
    successful: 24580,
    failed: 456,
    successRate: "98.2%",
    revenue: 315450,
    avgTransaction: 12.8,
  },
  {
    key: "2",
    gateway: "PayPal",
    successful: 18920,
    failed: 289,
    successRate: "98.5%",
    revenue: 242560,
    avgTransaction: 12.8,
  },
  {
    key: "3",
    gateway: "M-Pesa",
    successful: 15680,
    failed: 178,
    successRate: "98.9%",
    revenue: 201040,
    avgTransaction: 12.8,
  },
  {
    key: "4",
    gateway: "Card (Visa/MC)",
    successful: 12450,
    failed: 234,
    successRate: "98.1%",
    revenue: 159360,
    avgTransaction: 12.8,
  },
];

const deviceAnalytics = [
  {
    key: "1",
    platform: "Mobile",
    percentage: "65%",
    users: 81523,
    sessions: 1245680,
    avgSessionDuration: "38 min",
  },
  {
    key: "2",
    platform: "Web",
    percentage: "20%",
    users: 25084,
    sessions: 385230,
    avgSessionDuration: "52 min",
  },
  {
    key: "3",
    platform: "TV App",
    percentage: "10%",
    users: 12542,
    sessions: 156230,
    avgSessionDuration: "75 min",
  },
  {
    key: "4",
    platform: "Tablet",
    percentage: "5%",
    users: 6271,
    sessions: 78450,
    avgSessionDuration: "45 min",
  },
];

const searchAnalytics = [
  {
    key: "1",
    keyword: "action movies",
    searches: 8450,
    resultFound: 8320,
    noResults: 130,
    clickThroughRate: "78%",
    playConversion: "65%",
  },
  {
    key: "2",
    keyword: "indian cinema",
    searches: 6890,
    resultFound: 6780,
    noResults: 110,
    clickThroughRate: "82%",
    playConversion: "71%",
  },
  {
    key: "3",
    keyword: "latest series",
    searches: 5420,
    resultFound: 5340,
    noResults: 80,
    clickThroughRate: "75%",
    playConversion: "62%",
  },
  {
    key: "4",
    keyword: "hollywood",
    searches: 4890,
    resultFound: 4850,
    noResults: 40,
    clickThroughRate: "88%",
    playConversion: "76%",
  },
];

const liveStreamAnalytics = [
  {
    key: "1",
    event: "Live Concert - AR Rahman",
    concurrent: 45230,
    peak: 78560,
    avgDuration: "165 min",
    engagement: "89%",
    revenue: 12450,
  },
  {
    key: "2",
    event: "Movie Premiere - Avatar 3",
    concurrent: 125680,
    peak: 185340,
    avgDuration: "180 min",
    engagement: "94%",
    revenue: 35680,
  },
  {
    key: "3",
    event: "Cricket Match - India vs Pak",
    concurrent: 456230,
    peak: 785600,
    avgDuration: "240 min",
    engagement: "98%",
    revenue: 125600,
  },
];

const adAnalytics = [
  {
    key: "1",
    category: "Pre-roll Ads",
    impressions: 2456780,
    fillRate: "94.5%",
    cpm: 2.5,
    cpc: 0.35,
    ctr: "3.2%",
    revenue: 6141.95,
  },
  {
    key: "2",
    category: "Mid-roll Ads",
    impressions: 1245680,
    fillRate: "91.2%",
    cpm: 3.2,
    cpc: 0.42,
    ctr: "3.8%",
    revenue: 3986.18,
  },
  {
    key: "3",
    category: "Post-roll Ads",
    impressions: 856450,
    fillRate: "87.8%",
    cpm: 1.8,
    cpc: 0.28,
    ctr: "2.5%",
    revenue: 1541.61,
  },
];

const errorReports = [
  {
    key: "1",
    errorType: "Playback Failed",
    count: 456,
    percentage: "32%",
    severity: "High",
    resolution: "3.2 hrs avg",
  },
  {
    key: "2",
    errorType: "Buffering Issues",
    count: 345,
    percentage: "25%",
    severity: "Medium",
    resolution: "2.1 hrs avg",
  },
  {
    key: "3",
    errorType: "API Timeout",
    count: 234,
    percentage: "18%",
    severity: "High",
    resolution: "1.5 hrs avg",
  },
  {
    key: "4",
    errorType: "Login Error",
    count: 189,
    percentage: "14%",
    severity: "Medium",
    resolution: "45 min avg",
  },
];

const operationalReports = [
  {
    key: "1",
    task: "Content Uploads",
    completed: 485,
    pending: 23,
    avgTimeToPublish: "4.2 days",
    approvalRate: "98%",
  },
  {
    key: "2",
    task: "Support Tickets",
    resolved: 892,
    pending: 45,
    avgResolutionTime: "2.1 hours",
    satisfactionScore: "4.5/5",
  },
  {
    key: "3",
    task: "User Reports",
    reviewed: 234,
    actionTaken: 156,
    avgReviewTime: "6.5 hours",
    escalated: 23,
  },
];

const subscriptionAnalytics = [
  {
    key: "1",
    plan: "Basic Monthly",
    active: 28450,
    newSubs: 2850,
    renewals: 1890,
    revenue: 142250,
    arpu: 5.0,
    ltv: 450,
  },
  {
    key: "2",
    plan: "Premium Monthly",
    active: 12680,
    newSubs: 1250,
    renewals: 890,
    revenue: 203840,
    arpu: 16.08,
    ltv: 1440,
  },
  {
    key: "3",
    plan: "Premium Annual",
    active: 8920,
    newSubs: 680,
    renewals: 250,
    revenue: 80280,
    arpu: 9.0,
    ltv: 1800,
  },
  {
    key: "4",
    plan: "Family Plan",
    active: 5450,
    newSubs: 320,
    renewals: 180,
    revenue: 163500,
    arpu: 30.0,
    ltv: 3600,
  },
];

// KPI Card Component with trend
const KPICard = ({ title, value, suffix, prefix, change, icon }) => (
  <Paper
    withBorder
    p="md"
    style={{
      background: "linear-gradient(135deg, #023b67 0%, #012845 100%)",
      borderColor: "#27272a",
      borderRadius: "12px",
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
      <div>
        <Text size="xs" c="dimmed" mb={6}>{title}</Text>
        <Text size="xl" fw={700} c="white" mb={6}>
          {prefix}{typeof value === "number" ? value.toLocaleString() : value}{suffix}
        </Text>
        {change !== undefined && (
          <Text
            size="xs"
            style={{ color: change >= 0 ? "#4ade80" : "#f87171", display: "flex", alignItems: "center", gap: 4 }}
          >
            {change >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
            {Math.abs(change)}% from last period
          </Text>
        )}
      </div>
      {icon && <div style={{ fontSize: "32px", opacity: 0.3 }}>{icon}</div>}
    </div>
  </Paper>
);

// Column definitions
const contentColumns = [
  {
    title: "Content Title",
    dataIndex: "title",
    key: "title",
    width: 200,
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
  },
  {
    title: "Views",
    dataIndex: "views",
    key: "views",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Unique Viewers",
    dataIndex: "uniqueViewers",
    key: "uniqueViewers",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Watch Time (hrs)",
    dataIndex: "watchTime",
    key: "watchTime",
    render: (text) => (text / 3600).toFixed(2),
  },
  {
    title: "Completion %",
    dataIndex: "completionRate",
    key: "completionRate",
  },
  {
    title: "Avg Rating",
    dataIndex: "avgRating",
    key: "avgRating",
  },
];

const subscriptionColumns = [
  {
    title: "Plan Name",
    dataIndex: "plan",
    key: "plan",
  },
  {
    title: "Active Subscribers",
    dataIndex: "active",
    key: "active",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "New Subscriptions",
    dataIndex: "newSubs",
    key: "newSubs",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Renewals",
    dataIndex: "renewals",
    key: "renewals",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Revenue",
    dataIndex: "revenue",
    key: "revenue",
    render: (text) => `$${text.toLocaleString()}`,
  },
  {
    title: "ARPU",
    dataIndex: "arpu",
    key: "arpu",
    render: (text) => `$${text.toFixed(2)}`,
  },
  {
    title: "LTV",
    dataIndex: "ltv",
    key: "ltv",
    render: (text) => `$${text.toLocaleString()}`,
  },
];

const paymentColumns = [
  {
    title: "Payment Gateway",
    dataIndex: "gateway",
    key: "gateway",
  },
  {
    title: "Successful",
    dataIndex: "successful",
    key: "successful",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Failed",
    dataIndex: "failed",
    key: "failed",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Success Rate",
    dataIndex: "successRate",
    key: "successRate",
  },
  {
    title: "Revenue",
    dataIndex: "revenue",
    key: "revenue",
    render: (text) => `$${text.toLocaleString()}`,
  },
  {
    title: "Avg Transaction",
    dataIndex: "avgTransaction",
    key: "avgTransaction",
    render: (text) => `$${text.toFixed(2)}`,
  },
];

const deviceColumns = [
  {
    title: "Platform",
    dataIndex: "platform",
    key: "platform",
  },
  {
    title: "Market Share",
    dataIndex: "percentage",
    key: "percentage",
  },
  {
    title: "Users",
    dataIndex: "users",
    key: "users",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Sessions",
    dataIndex: "sessions",
    key: "sessions",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Avg Session Duration",
    dataIndex: "avgSessionDuration",
    key: "avgSessionDuration",
  },
];

const searchColumns = [
  {
    title: "Keyword",
    dataIndex: "keyword",
    key: "keyword",
  },
  {
    title: "Total Searches",
    dataIndex: "searches",
    key: "searches",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Results Found",
    dataIndex: "resultFound",
    key: "resultFound",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "No Results",
    dataIndex: "noResults",
    key: "noResults",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "CTR %",
    dataIndex: "clickThroughRate",
    key: "clickThroughRate",
  },
  {
    title: "Play Conversion %",
    dataIndex: "playConversion",
    key: "playConversion",
  },
];

const liveColumns = [
  {
    title: "Event",
    dataIndex: "event",
    key: "event",
  },
  {
    title: "Concurrent Viewers",
    dataIndex: "concurrent",
    key: "concurrent",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Peak Viewers",
    dataIndex: "peak",
    key: "peak",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Avg Duration",
    dataIndex: "avgDuration",
    key: "avgDuration",
  },
  {
    title: "Engagement %",
    dataIndex: "engagement",
    key: "engagement",
  },
  {
    title: "Revenue",
    dataIndex: "revenue",
    key: "revenue",
    render: (text) => `$${text.toLocaleString()}`,
  },
];

const adColumns = [
  {
    title: "Ad Type",
    dataIndex: "category",
    key: "category",
  },
  {
    title: "Impressions",
    dataIndex: "impressions",
    key: "impressions",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Fill Rate %",
    dataIndex: "fillRate",
    key: "fillRate",
  },
  {
    title: "CPM",
    dataIndex: "cpm",
    key: "cpm",
    render: (text) => `$${text.toFixed(2)}`,
  },
  {
    title: "CPC",
    dataIndex: "cpc",
    key: "cpc",
    render: (text) => `$${text.toFixed(2)}`,
  },
  {
    title: "CTR %",
    dataIndex: "ctr",
    key: "ctr",
  },
  {
    title: "Revenue",
    dataIndex: "revenue",
    key: "revenue",
    render: (text) => `$${text.toFixed(2)}`,
  },
];

const errorColumns = [
  {
    title: "Error Type",
    dataIndex: "errorType",
    key: "errorType",
  },
  {
    title: "Count",
    dataIndex: "count",
    key: "count",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Percentage",
    dataIndex: "percentage",
    key: "percentage",
  },
  {
    title: "Severity",
    dataIndex: "severity",
    key: "severity",
  },
  {
    title: "Avg Resolution Time",
    dataIndex: "resolution",
    key: "resolution",
  },
];

const operationalColumns = [
  {
    title: "Task",
    dataIndex: "task",
    key: "task",
  },
  {
    title: "Completed",
    dataIndex: "completed",
    key: "completed",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Pending",
    dataIndex: "pending",
    key: "pending",
    render: (text) => text.toLocaleString(),
  },
  {
    title: "Avg Time",
    dataIndex: "avgTimeToPublish",
    key: "avgTimeToPublish",
  },
  {
    title: "Success Rate",
    dataIndex: "approvalRate",
    key: "approvalRate",
  },
];

const Analytics = () => {
  const { type, id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = React.useState([
    dayjs().subtract(30, "days"),
    dayjs(),
  ]);
  const [activeTab, setActiveTab] = React.useState("executive");
  const pathname = useLocation().pathname;

  const startDate =
    searchParams.get("startDate") || dateRange[0].format("YYYY-MM-DD");
  const endDate =
    searchParams.get("endDate") || dateRange[1].format("YYYY-MM-DD");

  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ["analytics", type, id, startDate, endDate],
    queryFn: async () => {
      try {
        const url = `/admin/${type}-analytics/${id}?startDate=${startDate}&endDate=${endDate}`;
        const response = await axiosInstance.get(url);
        return response.data;
      } catch (error) {
        return null;
      }
    },
  });

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const dStart = dayjs(dates[0]);
      const dEnd = dayjs(dates[1]);
      setDateRange([dStart, dEnd]);
      setSearchParams({ startDate: dStart.format("YYYY-MM-DD"), endDate: dEnd.format("YYYY-MM-DD") });
    }
  };

  const handleExport = (format) => {
    notifications.show({ message: `Exporting data as ${format.toUpperCase()}...`, color: "green" });
  };

  const revenueColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
    },
    {
      title: "Amount",
      dataIndex: ["breakdown", "amount"],
      key: "amount",
      render: (amount) => `Tsh ${amount.toFixed(2)}`,
    },
    {
      title: "Type",
      dataIndex: ["breakdown", "type"],
      key: "type",
    },
    {
      title: "Total Comments",
      dataIndex: ["breakdown", "totalComments"],
      key: "totalComments",
    },
    {
      title: "Views",
      dataIndex: ["breakdown", "todayViewsSum"],
      key: "views",
    },
    {
      title: "Watch Time",
      dataIndex: ["breakdown", "todayWatchTimeSum"],
      key: "watchTime",
      render: (time) => `${(time / 3600).toFixed(2)} hrs`,
    },
  ];

  const kpis = generateDummyKPIs();
  const revenueData = generateRevenueData();
  const userGrowthData = generateUserGrowthData();
  const deviceData = generateDeviceDistribution();
  const contentPerformanceData = generateContentPerformanceData();
  const subscriptionTrendData = generateSubscriptionTrendData();
  const paymentMethodData = generatePaymentMethodData();

  const chartTooltipStyle = { background: "#18181b", border: "1px solid #27272a", borderRadius: "8px", color: "#fff" };
  const chartGridColor = "#27272a";
  const chartAxisColor = "#71717a";

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white m-0">Analytics & Reports</h1>
          <p className="text-zinc-400 text-sm mt-1 m-0">OTT Admin Panel Dashboard</p>
        </div>
        <Group gap="xs">
          <Button size="sm" leftSection={<FileSpreadsheet size={16} />} onClick={() => handleExport("excel")}>Excel</Button>
          <Button size="sm" leftSection={<FileText size={16} />} onClick={() => handleExport("pdf")}>PDF</Button>
          <Button size="sm" variant="outline" leftSection={<Download size={16} />} onClick={() => handleExport("csv")}>CSV</Button>
        </Group>
      </div>

      {/* Date Range Picker */}
      <Paper withBorder p="md" mb="lg">
        <Text size="xs" fw={500} c="dimmed" mb={8}>Select Date Range</Text>
        <DatePickerInput
          type="range"
          value={[dateRange[0].toDate(), dateRange[1].toDate()]}
          onChange={handleDateRangeChange}
          style={{ maxWidth: 400 }}
        />
      </Paper>

      {isLoading && !analyticsData ? (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      ) : (
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List mb="md" style={{ flexWrap: "wrap" }}>
            <Tabs.Tab value="executive">🎯 Executive</Tabs.Tab>
            <Tabs.Tab value="content">📺 Content</Tabs.Tab>
            <Tabs.Tab value="subscriptions">💳 Subscriptions</Tabs.Tab>
            <Tabs.Tab value="payments">💰 Payments</Tabs.Tab>
            <Tabs.Tab value="devices">📱 Devices</Tabs.Tab>
            <Tabs.Tab value="search">🔍 Search</Tabs.Tab>
            <Tabs.Tab value="live">🔴 Live</Tabs.Tab>
            <Tabs.Tab value="ads">📢 Ads</Tabs.Tab>
            <Tabs.Tab value="errors">⚠️ Errors</Tabs.Tab>
            <Tabs.Tab value="operations">⚙️ Operations</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="executive">
            <div className="flex flex-col gap-6">
              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                <KPICard title="Total Users" value={kpis.totalUsers} suffix=" users" change={kpis.userChange} />
                <KPICard title="DAU" value={kpis.dau} suffix=" users" change={kpis.dauChange} />
                <KPICard title="Monthly Revenue" prefix="$" value={kpis.revenueMonthly} change={kpis.revenueChange} />
                <KPICard title="Conversion Rate" value={kpis.conversionRate} suffix="%" change={kpis.conversionChange} />
              </SimpleGrid>

              <Paper withBorder p="md">
                <Text fw={600} mb="md">Revenue Trend</Text>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="colorDailyRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0361ab" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#0361ab" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis dataKey="date" stroke={chartAxisColor} />
                    <YAxis stroke={chartAxisColor} />
                    <RechartsTooltip contentStyle={chartTooltipStyle} />
                    <Area type="monotone" dataKey="daily" stroke="#0361ab" fillOpacity={1} fill="url(#colorDailyRevenue)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>

              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
                <Paper withBorder p="md">
                  <Text fw={600} mb="md">User Growth</Text>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={userGrowthData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                      <XAxis dataKey="date" stroke={chartAxisColor} />
                      <YAxis stroke={chartAxisColor} />
                      <RechartsTooltip contentStyle={chartTooltipStyle} />
                      <Legend />
                      <Bar dataKey="free" stackId="a" fill="#0361ab" name="Free" />
                      <Bar dataKey="paid" stackId="a" fill="#2e80ff" name="Paid" />
                      <Bar dataKey="premium" stackId="a" fill="#8ab8ff" name="Premium" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>

                <Paper withBorder p="md">
                  <Text fw={600} mb="md">Platform Distribution</Text>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={deviceData} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}%`} outerRadius={80} dataKey="value">
                        {deviceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip contentStyle={chartTooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                <Paper withBorder p="md" style={{ textAlign: "center" }}>
                  <Text size="xs" c="dimmed" mb={4}>Total Subscribers</Text>
                  <Text size="xl" fw={700} c="blue">{kpis.totalSubscribers.toLocaleString()} users</Text>
                </Paper>
                <Paper withBorder p="md" style={{ textAlign: "center" }}>
                  <Text size="xs" c="dimmed" mb={4}>Watch Hours</Text>
                  <Text size="xl" fw={700} c="green">{kpis.totalWatchHours.toLocaleString()} hrs</Text>
                </Paper>
                <Paper withBorder p="md" style={{ textAlign: "center" }}>
                  <Text size="xs" c="dimmed" mb={4}>Avg Watch Time</Text>
                  <Text size="xl" fw={700} c="yellow">{kpis.avgWatchTime} min</Text>
                </Paper>
                <Paper withBorder p="md" style={{ textAlign: "center" }}>
                  <Text size="xs" c="dimmed" mb={4}>Free vs Paid</Text>
                  <Text size="xl" fw={700} c="violet">Paid: {((kpis.paidUsers / kpis.totalSubscribers) * 100).toFixed(1)}%</Text>
                </Paper>
              </SimpleGrid>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="content">
            <div className="flex flex-col gap-4">
              <Paper withBorder p="md">
                <Text fw={600} mb="md">Content Performance by Genre</Text>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={contentPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis dataKey="name" stroke={chartAxisColor} />
                    <YAxis yAxisId="left" stroke={chartAxisColor} />
                    <YAxis yAxisId="right" orientation="right" stroke={chartAxisColor} />
                    <RechartsTooltip contentStyle={chartTooltipStyle} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="views" fill="#0361ab" name="Views" />
                    <Bar yAxisId="right" dataKey="revenue" fill="#2e80ff" name="Revenue ($)" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
              <Paper withBorder p="md">
                <Text fw={600} mb="md">Top Performing Content</Text>
                <DataTable dataSource={contentAnalytics} columns={contentColumns} pagination={{ pageSize: 10 }} scroll={{ x: 1000 }} rowKey="key" />
              </Paper>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="subscriptions">
            <div className="flex flex-col gap-4">
              <Paper withBorder p="md">
                <Text fw={600} mb="md">Subscription Trends</Text>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={subscriptionTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis dataKey="month" stroke={chartAxisColor} />
                    <YAxis stroke={chartAxisColor} />
                    <RechartsTooltip contentStyle={chartTooltipStyle} />
                    <Legend />
                    <Line type="monotone" dataKey="basic" stroke="#0361ab" name="Basic" />
                    <Line type="monotone" dataKey="premium" stroke="#2e80ff" name="Premium" />
                    <Line type="monotone" dataKey="annual" stroke="#5c9cff" name="Annual" />
                    <Line type="monotone" dataKey="family" stroke="#8ab8ff" name="Family" />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
              <Paper withBorder p="md">
                <Text fw={600} mb="md">Subscription Plan Performance</Text>
                <DataTable dataSource={subscriptionAnalytics} columns={subscriptionColumns} pagination={false} scroll={{ x: 1000 }} rowKey="key" />
              </Paper>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="payments">
            <div className="flex flex-col gap-4">
              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
                <Paper withBorder p="md">
                  <Text fw={600} mb="md">Payment Method Distribution</Text>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={paymentMethodData} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}%`} outerRadius={80} dataKey="value">
                        {paymentMethodData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip contentStyle={chartTooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
                <Paper withBorder p="md">
                  <Text fw={600} mb="md">Gateway Performance</Text>
                  {paymentAnalytics.map((payment) => (
                    <div key={payment.key} className="mb-4 p-4 rounded-lg bg-zinc-800/50">
                      <div className="flex justify-between items-center mb-2">
                        <Text fw={500}>{payment.gateway}</Text>
                        <Badge color="green">{payment.successRate} Success</Badge>
                      </div>
                      <Progress value={parseFloat(payment.successRate)} color="green" size="sm" />
                      <Text size="xs" c="dimmed" mt={6}>
                        {payment.successful.toLocaleString()} successful · {payment.failed} failed
                      </Text>
                    </div>
                  ))}
                </Paper>
              </SimpleGrid>
              <Paper withBorder p="md">
                <Text fw={600} mb="md">Payment Transactions</Text>
                <DataTable dataSource={paymentAnalytics} columns={paymentColumns} pagination={false} scroll={{ x: 1000 }} rowKey="key" />
              </Paper>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="devices">
            <Paper withBorder p="md">
              <Text fw={600} mb="md">Platform Analytics</Text>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md" mb="xl">
                {deviceAnalytics.map((device, index) => (
                  <Paper withBorder p="md" key={device.key} style={{ textAlign: "center", borderLeft: `3px solid ${COLORS[index % COLORS.length]}` }}>
                    <Text fw={600} mb={4}>{device.platform}</Text>
                    <Text size="xl" fw={700} style={{ color: COLORS[index % COLORS.length] }}>{device.percentage}</Text>
                    <Text size="xs" c="dimmed" mt={4}>{device.users.toLocaleString()} users</Text>
                  </Paper>
                ))}
              </SimpleGrid>
              <DataTable dataSource={deviceAnalytics} columns={deviceColumns} pagination={false} rowKey="key" />
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="search">
            <Paper withBorder p="md">
              <Text fw={600} mb="md">Top Search Keywords & Performance</Text>
              <DataTable dataSource={searchAnalytics} columns={searchColumns} pagination={false} scroll={{ x: 1000 }} rowKey="key" />
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="live">
            <Paper withBorder p="md">
              <Text fw={600} mb="md">Live Streaming Events</Text>
              <DataTable dataSource={liveStreamAnalytics} columns={liveColumns} pagination={false} scroll={{ x: 1000 }} rowKey="key" />
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="ads">
            <Paper withBorder p="md">
              <Text fw={600} mb="md">Advertisement Performance</Text>
              <DataTable dataSource={adAnalytics} columns={adColumns} pagination={false} scroll={{ x: 1000 }} rowKey="key" />
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="errors">
            <Paper withBorder p="md">
              <Text fw={600} mb="md">Error Reports</Text>
              <DataTable dataSource={errorReports} columns={errorColumns} pagination={false} rowKey="key" />
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="operations">
            <Paper withBorder p="md">
              <Text fw={600} mb="md">Operational Reports</Text>
              <DataTable dataSource={operationalReports} columns={operationalColumns} pagination={false} scroll={{ x: 1000 }} rowKey="key" />
            </Paper>
          </Tabs.Panel>
        </Tabs>
      )}

      {analyticsData?.owner === "Creator" && analyticsData?.revenue && !pathname.includes("episode") && (
        <Paper withBorder p="md" mt="lg">
          <Text fw={600} mb="md">Revenue Breakdown</Text>
          <DataTable dataSource={analyticsData.revenue} columns={revenueColumns} rowKey={(record) => record.date} pagination={false} />
        </Paper>
      )}
    </div>
  );
};

export default Analytics;
